/**
 * Infinite Scroll for Shopify Dawn Theme
 * Automatically loads next page of products when scrolling near the bottom
 */

class InfiniteScroll {
  constructor() {
    this.productGrid = document.getElementById('product-grid');
    this.scrollData = document.getElementById('infinite-scroll-data');
    this.loadingIndicator = document.getElementById('infinite-scroll-loading');
    this.endMessage = document.getElementById('infinite-scroll-end');
    
    // Redirect paginated URLs to page 1 (except for bots)
    this.handlePaginatedURL();
    
    if (!this.productGrid || !this.scrollData) {
      return; // Exit if not on a collection page with pagination
    }

    this.isLoading = false;
    this.hasMorePages = true;
    this.currentPage = parseInt(this.scrollData.dataset.currentPage);
    this.totalPages = parseInt(this.scrollData.dataset.totalPages);
    this.nextUrl = this.scrollData.dataset.nextUrl;
    
    // Preload cache for background fetching
    this.preloadedPage = null;
    this.isPreloading = false;
    
    // Configuration - VERY aggressive for small batches (12 products)
    this.loadTriggerDistance = 1500; // Show products when 1500px from bottom
    this.preloadTriggerDistance = 3000; // Start preloading when 3000px from bottom (very early!)
    this.throttleDelay = 100; // Check scroll position every 100ms for fast response
    
    this.init();
  }

  handlePaginatedURL() {
    // Check if we're on a paginated URL (page=2, page=3, etc.)
    const urlParams = new URLSearchParams(window.location.search);
    const pageParam = urlParams.get('page');
    
    // Only redirect if it's NOT a bot/crawler
    const isBot = /bot|crawler|spider|crawling/i.test(navigator.userAgent);
    
    if (pageParam && pageParam !== '1' && !isBot) {
      // Remove the page parameter and redirect to page 1
      urlParams.delete('page');
      const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
      window.location.replace(newUrl);
    }
    
    // Add canonical tag pointing to page 1 if it doesn't exist
    if (!document.querySelector('link[rel="canonical"]')) {
      const canonical = document.createElement('link');
      canonical.rel = 'canonical';
      urlParams.delete('page');
      canonical.href = window.location.origin + window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
      document.head.appendChild(canonical);
    }
  }

  init() {
    // Check if we're already on the last page
    if (this.currentPage >= this.totalPages) {
      this.hasMorePages = false;
      console.log('Already on last page, infinite scroll disabled');
      return;
    }

    console.log(`Infinite Scroll initialized: Page ${this.currentPage} of ${this.totalPages}`);
    console.log('Next URL:', this.nextUrl);

    // Immediately start preloading page 2 when the collection first loads
    // This ensures it's ready by the time user scrolls down
    if (this.currentPage === 1 && this.nextUrl) {
      setTimeout(() => {
        console.log('🚀 Auto-preloading page 2 on initial load...');
        this.preloadNextPage();
      }, 500); // Small delay to let page finish loading
    }

    // Set up scroll listener with throttling
    this.handleScroll = this.throttle(this.checkScrollPosition.bind(this), this.throttleDelay);
    window.addEventListener('scroll', this.handleScroll);
    
    // Listen for filter/sort changes to reset infinite scroll
    document.addEventListener('facet-filters-changed', () => {
      this.reset();
    });
  }

  checkScrollPosition() {
    if (!this.hasMorePages) {
      return;
    }

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    
    // Calculate distance from bottom (in pixels)
    const distanceFromBottom = documentHeight - (scrollTop + windowHeight);
    
    // Preload trigger: Start fetching in background when further away
    if (distanceFromBottom <= this.preloadTriggerDistance && !this.isPreloading && !this.preloadedPage && !this.isLoading) {
      this.preloadNextPage();
    }
    
    // Load trigger: Actually display products when closer
    if (distanceFromBottom <= this.loadTriggerDistance && !this.isLoading) {
      this.loadNextPage();
    }
  }

  async preloadNextPage() {
    if (!this.nextUrl || this.isPreloading || this.preloadedPage) {
      return;
    }

    this.isPreloading = true;
    console.log('🔄 Preloading next page in background...');

    try {
      const response = await fetch(this.nextUrl);
      const html = await response.text();
      
      // Store the preloaded data
      this.preloadedPage = {
        html: html,
        url: this.nextUrl
      };
      
      console.log('✅ Next page preloaded and cached');
    } catch (error) {
      console.error('Error preloading next page:', error);
    } finally {
      this.isPreloading = false;
    }
  }

  async loadNextPage() {
    if (!this.nextUrl || this.isLoading) {
      return;
    }

    this.isLoading = true;
    
    // Show loading immediately for instant user feedback
    this.showLoading();

    try {
      let html;
      
      // Use preloaded page if available (instant!)
      if (this.preloadedPage && this.preloadedPage.url === this.nextUrl) {
        console.log('⚡ Using preloaded page - instant load!');
        html = this.preloadedPage.html;
        this.preloadedPage = null; // Clear the cache
      } else {
        // Fetch normally if not preloaded
        console.log('📥 Fetching page...');
        const response = await fetch(this.nextUrl);
        html = await response.text();
      }
      
      // Extract just the product grid section (faster than parsing entire HTML)
      const productGridMatch = html.match(/<ul[^>]*id="product-grid"[^>]*>([\s\S]*?)<\/ul>/);
      const paginationMatch = html.match(/<div[^>]*id="infinite-scroll-data"[^>]*data-current-page="(\d+)"[^>]*data-total-pages="(\d+)"[^>]*data-next-url="([^"]*)"[^>]*>/);
      
      if (productGridMatch && paginationMatch) {
        // Parse only the product grid HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = productGridMatch[0];
        const newProducts = tempDiv.querySelectorAll('.grid__item');
        
        if (newProducts.length > 0) {
          // Append new products to the grid
          const fragment = document.createDocumentFragment();
          newProducts.forEach((product, index) => {
            // Update animation order for smooth entry
            const animationOrder = this.productGrid.children.length + index + 1;
            product.style.setProperty('--animation-order', animationOrder);
            fragment.appendChild(product);
          });
          this.productGrid.appendChild(fragment);

          // Update pagination data from regex match (faster than DOM parsing)
          this.currentPage = parseInt(paginationMatch[1]);
          this.totalPages = parseInt(paginationMatch[2]);
          this.nextUrl = paginationMatch[3];
          
          // Update our data element
          this.scrollData.dataset.currentPage = this.currentPage;
          this.scrollData.dataset.nextUrl = this.nextUrl;
          
          console.log(`Loaded page ${this.currentPage} of ${this.totalPages}`);
          
          // Check if we've reached the last page
          if (this.currentPage >= this.totalPages || !this.nextUrl) {
            this.hasMorePages = false;
            this.showEndMessage();
            console.log('Reached last page');
          }

          // Re-initialize any Dawn theme features for new products
          this.reinitializeFeatures();
        } else {
          this.hasMorePages = false;
          this.showEndMessage();
          console.log('No products found in response');
        }
      } else {
        this.hasMorePages = false;
        this.showEndMessage();
        console.log('Could not extract product data from response');
      }
    } catch (error) {
      console.error('Error loading next page:', error);
      this.hasMorePages = false;
    } finally {
      this.isLoading = false;
      this.hideLoading();
    }
  }

  reinitializeFeatures() {
    // Reinitialize quick add buttons if enabled (only if the feature exists)
    if (window.Shopify && window.Shopify.PaymentButton) {
      window.Shopify.PaymentButton.init();
    }

    // Dawn's native lazy loading handles images automatically - no need to manually trigger
    // Scroll animations will trigger automatically via Intersection Observer
    
    // Immediately start preloading the NEXT page in background
    if (this.hasMorePages && this.nextUrl) {
      // Small delay to let current products render first
      setTimeout(() => {
        this.preloadNextPage();
      }, 100);
    }
  }

  showLoading() {
    if (this.loadingIndicator) {
      this.loadingIndicator.classList.add('active');
    }
  }

  hideLoading() {
    if (this.loadingIndicator) {
      this.loadingIndicator.classList.remove('active');
    }
  }

  showEndMessage() {
    if (this.endMessage) {
      this.endMessage.classList.add('active');
    }
  }

  reset() {
    // Remove scroll listener
    window.removeEventListener('scroll', this.handleScroll);
    
    // Reset state
    this.isLoading = false;
    this.hasMorePages = true;
    
    // Hide loading and end message
    this.hideLoading();
    if (this.endMessage) {
      this.endMessage.classList.remove('active');
    }
    
    // Reinitialize with new data
    setTimeout(() => {
      this.scrollData = document.getElementById('infinite-scroll-data');
      if (this.scrollData) {
        this.currentPage = parseInt(this.scrollData.dataset.currentPage);
        this.totalPages = parseInt(this.scrollData.dataset.totalPages);
        this.nextUrl = this.scrollData.dataset.nextUrl;
        this.init();
      }
    }, 500);
  }

  // Throttle function to limit scroll event frequency
  throttle(func, wait) {
    let timeout;
    let previous = 0;
    
    return function() {
      const now = Date.now();
      const remaining = wait - (now - previous);
      const context = this;
      const args = arguments;
      
      if (remaining <= 0 || remaining > wait) {
        if (timeout) {
          clearTimeout(timeout);
          timeout = null;
        }
        previous = now;
        func.apply(context, args);
      } else if (!timeout) {
        timeout = setTimeout(() => {
          previous = Date.now();
          timeout = null;
          func.apply(context, args);
        }, remaining);
      }
    };
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new InfiniteScroll();
  });
} else {
  new InfiniteScroll();
}
